import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Heading,
  Checkbox,
  useToast,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SendEmail from "../components/SendEmail";

const Signup = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    nickname: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "이름을 입력해주세요";
    }
    if (!formData.nickname.trim()) {
      newErrors.nickname = "닉네임을 입력해주세요";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "핸드폰 번호를 입력해주세요";
    } else if (!/^0[0-9]{9,10}$/.test(formData.phone)) {
      newErrors.phone = "올바른 핸드폰 번호를 입력해주세요";
    }
    if (!formData.email.trim()) {
      newErrors.email = "이메일을 입력해주세요";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "올바른 이메일 주소를 입력해주세요";
    }
    if (!formData.password.trim()) {
      newErrors.password = "비밀번호를 입력해주세요";
    } else if (formData.password.length < 6) {
      newErrors.password = "비밀번호는 6자 이상이어야 합니다";
    } else if (
      !/(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>[\]\\/~`';_+=-]).+/.test(
        formData.password
      )
    ) {
      newErrors.password =
        "비밀번호는 소문자, 숫자, 특수기호를 각각 1개 이상 포함해야 합니다";
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "비밀번호 확인을 입력해주세요";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호가 일치하지 않습니다";
    }
    if (!agreements.terms || !agreements.privacy) {
      newErrors.agreements = "필수 약관에 동의해주세요";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateEmail = async (email) => {
    const { data, error } = await supabase.rpc("check_user_field_taken", {
      field: "email",
      value: email,
    });

    if (error) {
      console.error("이메일 중복 체크 중 오류 발생:", error);
      return false;
    }

    return data === true;
  };

  const checkDuplicatePhone = async (phone) => {
    const { data, error } = await supabase.rpc("check_user_field_taken", {
      field: "phone_number",
      value: phone,
    });

    if (error) {
      console.error("핸드폰번호 중복 체크 중 오류 발생:", error);
      return false;
    }

    return data === true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "phone") {
      processedValue = value.replace(/-/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleCheckboxChange = (name) => {
    setAgreements((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "입력 오류",
        description: "모든 필수 항목을 올바르게 입력해주세요.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 이메일 중복 체크
    const isEmailDuplicate = await checkDuplicateEmail(formData.email);
    if (isEmailDuplicate) {
      toast({
        title: "회원가입 실패",
        description: "이미 사용 중인 이메일입니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 핸드폰번호 중복 체크 (저장할 때와 동일한 형식으로 체크)
    const phoneForCheck = formData.phone.startsWith("0")
      ? formData.phone.substring(1)
      : formData.phone;
    const isPhoneDuplicate = await checkDuplicatePhone(phoneForCheck);
    if (isPhoneDuplicate) {
      toast({
        title: "회원가입 실패",
        description: "이미 사용 중인 핸드폰 번호입니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      // 1. Supabase Authentication에 사용자 등록
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) throw authError;

      // 2. user 테이블에 사용자 정보 저장 (핸드폰 번호 앞의 0 제거)
      const { error: dbError } = await supabase.from("user").insert([
        {
          name: formData.name,
          nickname: formData.nickname,
          phone_number: formData.phone.startsWith("0")
            ? formData.phone.substring(1)
            : formData.phone,
          email: formData.email,
          agreement_privacy: true,
          agreement_terms: true,
          is_migration: true,
          admin: false,
          img_profile:
            "https://plpl-file-from-bubble.s3.ap-northeast-2.amazonaws.com/user-profile-image/plpl.png",
        },
      ]);

      if (dbError) throw dbError;

      // 3. 이메일 전송
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: [formData.email],
        subject: `[플플 회원] 새로운 회원가입 / ${formData?.email}`,
        message: `<p>${formData.email}이 회원가입했습니다.</p>`,
      });

      toast({
        title: "회원가입 성공",
        description: "회원가입이 완료되었습니다. 로그인해주세요.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/login");
    } catch (error) {
      console.error("Error during signup:", error);
      toast({
        title: "회원가입 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <Box bg="white" p={8} borderRadius="xl" boxShadow="lg">
        <VStack spacing={6} align="stretch">
          <Heading textAlign="center" size="lg" mb={2}>
            회원가입
          </Heading>

          <FormControl isRequired isInvalid={errors.name}>
            <FormLabel>이름</FormLabel>
            <Input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="이름을 입력하세요"
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={errors.nickname}>
            <FormLabel>닉네임</FormLabel>
            <Input
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              placeholder="닉네임을 입력하세요"
            />
            <FormErrorMessage>{errors.nickname}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={errors.phone}>
            <FormLabel>핸드폰 번호</FormLabel>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="핸드폰 번호를 입력하세요 (01012345678)"
            />
            <FormErrorMessage>{errors.phone}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={errors.email}>
            <FormLabel>이메일</FormLabel>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="이메일을 입력하세요"
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={errors.password}>
            <FormLabel>비밀번호</FormLabel>
            <InputGroup>
              <Input
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="비밀번호를 입력하세요"
              />
              <InputRightElement>
                <IconButton
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword(!showPassword)}
                  variant="ghost"
                  aria-label={
                    showPassword ? "비밀번호 숨기기" : "비밀번호 보기"
                  }
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={errors.confirmPassword}>
            <FormLabel>비밀번호 확인</FormLabel>
            <InputGroup>
              <Input
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="비밀번호를 다시 입력하세요"
              />
              <InputRightElement>
                <IconButton
                  icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  variant="ghost"
                  aria-label={
                    showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"
                  }
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
          </FormControl>

          <Text color="red.500" fontSize="sm">
            이메일과 핸드폰 번호는 정상을 확인한 후 각종 알림 등에 사용되니
            올바른 정보를 입력해주세요.
          </Text>

          <VStack spacing={2} align="stretch">
            <Checkbox
              isChecked={agreements.terms}
              onChange={() => handleCheckboxChange("terms")}
            >
              (필수) 이용 약관에 동의합니다.
            </Checkbox>
            <Checkbox
              isChecked={agreements.privacy}
              onChange={() => handleCheckboxChange("privacy")}
            >
              (필수) 개인정보처리방침에 동의합니다.
            </Checkbox>
            {errors.agreements && (
              <Text color="red.500" fontSize="sm">
                {errors.agreements}
              </Text>
            )}
          </VStack>

          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            회원가입
          </Button>

          <Text textAlign="center">
            이미 회원이신가요?{" "}
            <Button
              variant="link"
              colorScheme="blue"
              onClick={() => navigate("/login")}
            >
              로그인
            </Button>
          </Text>
        </VStack>
      </Box>
    </Container>
  );
};

export default Signup;
